import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Upload, Play, Pause, Heart, MessageCircle, Share2, Bookmark, 
  Flag, ChevronLeft, ChevronRight, MoreVertical, ExternalLink, Film,
  Newspaper, Video, X, Image as ImageIcon, Mic, Clock, Info, CheckCircle, Volume2, Tv, BookOpen
} from 'lucide-react';
import Navigation from '../components/Navigation';
import PodcastUploader from '../components/PodcastUploader';
import AdDisplay from '../components/AdDisplay';
import { getMediaUrl } from '../utils/mediaUrl';
import { shareContent, buildShareUrl, incrementShareCount } from '../utils/shareContent';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import VerifiedBadge from '../components/VerifiedBadge';

const Explorer = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  
  // States for different sections
  const [podcasts, setPodcasts] = useState([]);
  const [freeFilms, setFreeFilms] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploader, setShowUploader] = useState(false);
  const [newsAudioLoading, setNewsAudioLoading] = useState(null);
  const [newsAudioPlaying, setNewsAudioPlaying] = useState(null);
  const [whereToWatch, setWhereToWatch] = useState({ country: 'FR', movies: [], tv: [] });
  const [selectedWatch, setSelectedWatch] = useState(null); // {item, providers}
  const [loadingWatch, setLoadingWatch] = useState(false);
  const newsAudioRef = React.useRef(null);
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadAllContent();
    // Re-fetch when language changes — free films, news, etc. are translated server-side.
  }, [language]); // eslint-disable-line

  // Deep-link: when arriving at /explorer?podcast=ID or ?article=ID, scroll to + highlight the card.
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const podcastId = params.get('podcast');
    const articleId = params.get('article');
    const targetId = podcastId ? `podcast-${podcastId}` : (articleId ? `article-${articleId}` : null);
    if (!targetId) return;

    // Wait one frame to ensure cards are rendered
    const t = setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-magenta-400', 'transition-all');
        setTimeout(() => {
          el.classList.remove('ring-4', 'ring-magenta-400');
        }, 2400);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [loading, podcasts, newsArticles]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch();
      } else {
        setSearchResults(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // performSearch is stable

  const loadAllContent = async () => {
    setLoading(true);
    try {
      const userCountry = user?.preferred_country;
      const wtwUrl = userCountry
        ? `/api/explorer/where-to-watch/popular?country=${userCountry}`
        : '/api/explorer/where-to-watch/popular';
      const [podcastsRes, freeFilmsRes, newsRes, wtwRes] = await Promise.allSettled([
        axios.get('/api/podcasts'),
        axios.get('/api/explorer/free-films', { params: { lang: language } }),
        axios.get(`/api/news/feed?lang=${language}`),
        axios.get(wtwUrl)
      ]);

      if (podcastsRes.status === 'fulfilled') setPodcasts(podcastsRes.value.data);
      if (freeFilmsRes.status === 'fulfilled') setFreeFilms(freeFilmsRes.value.data);
      if (newsRes.status === 'fulfilled') setNewsArticles(newsRes.value.data);
      if (wtwRes.status === 'fulfilled') setWhereToWatch(wtwRes.value.data);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const openWatchProviders = async (item) => {
    setLoadingWatch(true);
    setSelectedWatch({ item, providers: null });
    try {
      const userCountry = user?.preferred_country || whereToWatch.country;
      const res = await axios.get(`/api/explorer/where-to-watch?type=${item.type}&id=${item.id}&country=${userCountry}`);
      setSelectedWatch({ item, providers: res.data });
    } catch (e) {
      toast.error('Impossible de charger les plateformes');
      setSelectedWatch(null);
    } finally {
      setLoadingWatch(false);
    }
  };

  const performSearch = async () => {
    setSearching(true);
    try {
      const response = await axios.get(`/api/explorer/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handlePodcastCreated = () => {
    setShowUploader(false);
    loadAllContent();
    toast.success('Podcast publié avec succès!');
  };

  const playNewsAudio = async (article) => {
    // Stop current audio
    if (newsAudioRef.current) {
      newsAudioRef.current.pause();
      newsAudioRef.current.currentTime = 0;
    }
    if (newsAudioPlaying === article.id) {
      setNewsAudioPlaying(null);
      return;
    }
    setNewsAudioLoading(article.id);
    try {
      const text = `${article.title}. ${article.description || ''}`;
      const res = await axios.post(`/api/news/${article.id}/tts`, { text });
      if (res.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${res.data.audio}`);
        newsAudioRef.current = audio;
        audio.play();
        setNewsAudioPlaying(article.id);
        audio.onended = () => setNewsAudioPlaying(null);
      }
    } catch {
      toast.error('Erreur audio');
    } finally {
      setNewsAudioLoading(null);
    }
  };


  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gradient-to-br from-gray-900 via-black to-gray-900' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen text-white pb-24 ${isDark ? 'bg-gradient-to-br from-gray-900 via-black to-gray-900' : ''}`} style={!isDark ? { background: 'var(--theme-bg)' } : {}}>
      {/* Header (scrolls away with the page) */}
      <div className={`relative z-50 backdrop-blur-md border-b border-white/10 ${isDark ? 'bg-black/80' : 'bg-white/10'}`}>
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-violet-400 to-magenta-400 bg-clip-text text-transparent">
                {t("explorer.title")}
              </h1>
              <p className="text-gray-400 text-sm sm:text-base mt-1">
                {t("explorer.subtitle")}
              </p>
            </div>
          </div>

          {/* Global Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("explorer.searchPlaceholder")}
              className="w-full pl-12 pr-10 py-3 bg-white/10 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults(null);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {searchResults && (
            <SearchResultsDropdown 
              results={searchResults} 
              onClose={() => setSearchResults(null)}
              navigate={navigate}
            />
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 space-y-10">
        
        {/* FILMS LIBRES DE DROIT Section (Admin only can add) */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Film className="w-6 h-6 text-cyan-400" />
              {t("explorer.freeFilms")}
            </h2>
          </div>
          <HorizontalScrollSection>
            {freeFilms.length === 0 ? (
              <EmptyState message="Aucun film disponible" />
            ) : (
              freeFilms.map((film) => (
                <FreeFilmCard key={film.id} film={film} />
              ))
            )}
          </HorizontalScrollSection>
        </section>

        {/* WHERE TO WATCH Section - TMDB watch providers */}
        <section data-testid="where-to-watch-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Tv className="w-6 h-6 text-cyan-400" />
              {t('explorer.whereToWatch')}
            </h2>
            <span className="text-xs text-gray-400 bg-white/5 px-2 py-1 rounded-full">
              📍 {whereToWatch.country}
            </span>
          </div>
          <HorizontalScrollSection>
            {(whereToWatch.movies?.length || 0) + (whereToWatch.tv?.length || 0) === 0 ? (
              <EmptyState message="Chargement…" />
            ) : (
              [...(whereToWatch.movies || []), ...(whereToWatch.tv || [])].map((item) => (
                <button
                  key={`${item.type}-${item.id}`}
                  onClick={() => openWatchProviders(item)}
                  className="flex-shrink-0 w-32 sm:w-40 group text-left"
                  data-testid={`watch-card-${item.id}`}
                >
                  <div className="aspect-[2/3] rounded-xl overflow-hidden bg-white/5 border border-white/10 group-hover:border-cyan-500/50 transition-all relative">
                    {item.poster_url ? (
                      <img src={item.poster_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600">
                        <Film className="w-10 h-10" />
                      </div>
                    )}
                    <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] uppercase font-bold text-cyan-300">
                      {item.type === 'tv' ? 'Série' : 'Film'}
                    </div>
                    <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-yellow-500/90 rounded text-[10px] font-bold text-black">
                      ⭐ {item.vote_average}
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm mt-2 font-medium truncate">{item.title}</p>
                </button>
              ))
            )}
          </HorizontalScrollSection>
        </section>

        {/* ACTUS Section - Auto-curated news */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Newspaper className="w-6 h-6 text-orange-400" />
              {t("explorer.news")}
            </h2>
          </div>
          <HorizontalScrollSection>
            {newsArticles.length === 0 ? (
              <EmptyState message="Chargement des actualités..." />
            ) : (
              newsArticles.map((article) => (
                <div
                  key={article.id}
                  className="flex-shrink-0 w-72 bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all group"
                >
                  {article.image_url && (
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      {article.source_name && (
                        <span className="absolute bottom-2 left-2 text-[10px] bg-black/50 px-2 py-0.5 rounded-full text-white/80">
                          {article.source_name}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-3.5">
                    <h3 className="font-semibold text-sm line-clamp-3 mb-1.5 leading-snug">{article.title}</h3>
                    {article.description && (
                      <p className="text-xs text-gray-400 line-clamp-4 mb-3">{article.description}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => playNewsAudio(article)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          newsAudioPlaying === article.id
                            ? 'bg-orange-500 text-white'
                            : 'bg-white/10 hover:bg-orange-500/20 text-gray-300'
                        }`}
                        disabled={newsAudioLoading === article.id}
                        data-testid="news-listen-btn"
                      >
                        {newsAudioLoading === article.id ? (
                          <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : newsAudioPlaying === article.id ? (
                          <Volume2 className="w-3 h-3" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                        {newsAudioPlaying === article.id ? 'Stop' : 'Écouter'}
                      </button>
                      {article.source_url && (
                        <a
                          href={article.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-xs text-gray-300 transition-all"
                          onClick={(e) => e.stopPropagation()}
                          data-testid="news-source-link"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Source
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </HorizontalScrollSection>
          <audio ref={newsAudioRef} className="hidden" />
        </section>
      </div>

      {/* Modals */}
      {showUploader && (
        <PodcastUploader
          onClose={() => setShowUploader(false)}
          onSuccess={handlePodcastCreated}
        />
      )}

      {/* Where to Watch providers modal */}
      {selectedWatch && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[9999] flex items-center justify-center p-4" data-testid="watch-providers-modal" onClick={() => setSelectedWatch(null)}>
          <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Tv className="w-5 h-5 text-cyan-400" />
                {t('explorer.whereToWatch')}
              </h3>
              <button onClick={() => setSelectedWatch(null)} className="p-2 hover:bg-white/10 rounded-full" data-testid="close-watch-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex gap-3">
                {selectedWatch.item.poster_url && (
                  <img src={selectedWatch.item.poster_url} alt="" className="w-20 h-28 object-cover rounded-lg" />
                )}
                <div>
                  <h4 className="font-bold">{selectedWatch.item.title}</h4>
                  <p className="text-xs text-yellow-400 mt-1">⭐ {selectedWatch.item.vote_average}</p>
                  {selectedWatch.providers?.country && (
                    <p className="text-xs text-gray-400 mt-1">📍 {selectedWatch.providers.country}</p>
                  )}
                </div>
              </div>

              {loadingWatch && <p className="text-center text-gray-400 py-4">Chargement…</p>}
              {!loadingWatch && selectedWatch.providers && (
                <>
                  {['flatrate', 'free', 'ads', 'rent', 'buy'].map(kind => {
                    const items = selectedWatch.providers[kind];
                    if (!items || items.length === 0) return null;
                    const labels = { flatrate: 'Avec abonnement', free: 'Gratuit', ads: 'Avec pubs', rent: 'En location', buy: "À l'achat" };
                    return (
                      <div key={kind}>
                        <p className="text-xs uppercase text-gray-400 mb-2 font-semibold">{labels[kind]}</p>
                        <div className="grid grid-cols-4 gap-2">
                          {items.map(p => (
                            <div key={p.provider_id} className="bg-white/5 border border-white/10 rounded-xl p-2 text-center" data-testid={`provider-${p.provider_id}`}>
                              {p.logo_url && <img src={p.logo_url} alt={p.name} className="w-10 h-10 mx-auto rounded" />}
                              <p className="text-[10px] mt-1 truncate">{p.name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {(!selectedWatch.providers.flatrate?.length && !selectedWatch.providers.free?.length && !selectedWatch.providers.rent?.length && !selectedWatch.providers.buy?.length && !selectedWatch.providers.ads?.length) && (
                    <p className="text-sm text-gray-400 text-center py-4">
                      Pas encore disponible en streaming dans {selectedWatch.providers.country || 'votre pays'}.
                    </p>
                  )}
                  {selectedWatch.providers.link && (
                    <a
                      href={selectedWatch.providers.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full text-center py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 rounded-xl text-sm font-medium border border-cyan-500/30"
                    >
                      Voir sur JustWatch ↗
                    </a>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Navigation />
    </div>
  );
};

// ==================== COMPONENTS ====================

export const HorizontalScrollSection = ({ children }) => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ 
        left: direction === 'left' ? -350 : 350, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="relative group">
      <div
        ref={scrollRef}
        className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {children}
      </div>
    </div>
  );
};

const EmptyState = ({ message, fullWidth = false }) => (
  <div className={`flex-shrink-0 ${fullWidth ? 'w-full' : 'w-[300px]'} h-[200px] flex items-center justify-center bg-white/5 rounded-2xl border border-white/10`}>
    <p className="text-gray-500">{message}</p>
  </div>
);

// Search Results Dropdown
export const SearchResultsDropdown = ({ results, onClose, navigate }) => (
  <div className="absolute left-0 right-0 top-full mt-2 bg-gray-900 border border-white/10 rounded-2xl shadow-2xl max-h-96 overflow-y-auto z-[60]" data-testid="search-results">
    {results.users?.length > 0 && (
      <div className="p-3 border-b border-white/10">
        <h4 className="text-xs text-gray-400 uppercase mb-2">Utilisateurs</h4>
        {results.users.map(user => (
          <button
            key={user.id}
            onClick={() => { navigate(`/profile/${user.id}`); onClose(); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg"
            data-testid={`search-user-${user.id}`}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-magenta-600 rounded-full flex items-center justify-center overflow-hidden shrink-0">
              {user.profile_image_url ? (
                <img
                  src={getMediaUrl(user.profile_image_url)}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="text-left">
              <p className="font-medium">{user.name}</p>
              {user.username && <p className="text-xs text-gray-400">@{user.username}</p>}
            </div>
          </button>
        ))}
      </div>
    )}
    
    {results.gim_tolis?.length > 0 && (
      <div className="p-3 border-b border-white/10">
        <h4 className="text-xs text-gray-400 uppercase mb-2">Gim Tolis</h4>
        {results.gim_tolis.map(post => (
          <button
            key={post.id}
            onClick={() => { navigate(`/feed?tab=gimtoli&post=${post.id}`); onClose(); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg"
            data-testid={`search-gimtoli-${post.id}`}
          >
            <Mic className="w-8 h-8 text-magenta-400" />
            <div className="text-left">
              <p className="font-medium">{post.title || 'Gim Toli'}</p>
              <p className="text-xs text-gray-400 line-clamp-1">{post.transcription?.slice(0, 50)}...</p>
            </div>
          </button>
        ))}
      </div>
    )}

    {results.podcasts?.length > 0 && (
      <div className="p-3 border-b border-white/10">
        <h4 className="text-xs text-gray-400 uppercase mb-2">Podcasts</h4>
        {results.podcasts.map(podcast => (
          <button
            key={podcast.id}
            onClick={() => { navigate(`/feed?tab=studio&podcast=${podcast.id}`); onClose(); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg"
            data-testid={`search-podcast-${podcast.id}`}
          >
            <Mic className="w-8 h-8 text-violet-400" />
            <div className="text-left">
              <p className="font-medium">{podcast.title}</p>
              <p className="text-xs text-gray-400">{podcast.category}</p>
            </div>
          </button>
        ))}
      </div>
    )}

    {results.films?.length > 0 && (
      <div className="p-3">
        <h4 className="text-xs text-gray-400 uppercase mb-2">Films</h4>
        {results.films.map(film => (
          <button
            key={film.id}
            onClick={() => { navigate(`/films?film=${film.id}`); onClose(); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg"
            data-testid={`search-film-${film.id}`}
          >
            <Film className="w-8 h-8 text-cyan-400" />
            <div className="text-left">
              <p className="font-medium">{film.title}</p>
            </div>
          </button>
        ))}
      </div>
    )}

    {results.studio_stories?.length > 0 && (
      <div className="p-3 border-t border-white/10">
        <h4 className="text-xs text-gray-400 uppercase mb-2">Jeux vocaux</h4>
        {results.studio_stories.map(s => (
          <button
            key={s.id}
            onClick={() => { navigate(`/feed?tab=studio&story=${s.id}`); onClose(); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg"
            data-testid={`search-story-${s.id}`}
          >
            <BookOpen className="w-8 h-8 text-fuchsia-400" />
            <div className="text-left min-w-0">
              <p className="font-medium truncate">{s.title}</p>
              <p className="text-xs text-gray-400 truncate">{s.turns_count} tour(s) • {s.status === 'open' ? 'En cours' : 'Fermée'}</p>
            </div>
          </button>
        ))}
      </div>
    )}

    {results.photos?.length > 0 && (
      <div className="p-3 border-t border-white/10">
        <h4 className="text-xs text-gray-400 uppercase mb-2">Photos partagées</h4>
        {results.photos.map(p => (
          <button
            key={p.id}
            onClick={() => { navigate(`/profile/${p.user?.id || p.user_id}`); onClose(); }}
            className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg"
            data-testid={`search-photo-${p.id}`}
          >
            <img src={(p.image_urls || [])[0]} alt="" className="w-8 h-8 rounded-lg object-cover" />
            <div className="text-left min-w-0">
              <p className="font-medium truncate">{p.caption || 'Photo'}</p>
              <p className="text-xs text-gray-400 truncate">{p.user?.name || ''} · ❤️ {p.likes_count || 0}</p>
            </div>
          </button>
        ))}
      </div>
    )}

    {(!results.users?.length && !results.gim_tolis?.length && !results.podcasts?.length && !results.films?.length && !results.studio_stories?.length && !results.photos?.length) && (
      <div className="p-6 text-center text-gray-400">
        Aucun résultat trouvé
      </div>
    )}
  </div>
);

// Podcast Card Component
const PodcastCard = ({ podcast, onUpdate }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [likesCount, setLikesCount] = useState(podcast.likes_count || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      if (audioRef.current.duration) {
        setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleLike = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/podcasts/${podcast.id}/like`);
      setLiked(!liked);
      setLikesCount(prev => liked ? prev - 1 : prev + 1);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/podcasts/${podcast.id}/save`);
      setSaved(!saved);
      toast.success(saved ? 'Retiré des favoris' : 'Enregistré');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleReport = async () => {
    try {
      await axios.post(`/api/podcasts/${podcast.id}/report`);
      toast.success('Podcast signalé');
      setShowMenu(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleShare = async () => {
    try {
      const result = await shareContent({
        title: podcast.title || 'Podcast',
        text: `Écoute ce podcast sur Destine : ${podcast.title}`,
        url: buildShareUrl('podcast', podcast.id),
      });
      if (result.shared && !result.cancelled) {
        await incrementShareCount('podcast', podcast.id);
      }
    } catch (_e) {
      // user cancellation already handled by shareContent
    }
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div id={`podcast-${podcast.id}`} className="flex-shrink-0 w-[280px] snap-center scroll-mt-24 rounded-2xl">
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-violet-500/30 transition-all h-full">
        <div className="relative aspect-square">
          {podcast.cover_image_url ? (
            <img
              src={getMediaUrl(podcast.cover_image_url)}
              alt={podcast.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-violet-600 to-magenta-600 flex items-center justify-center">
              <Mic className="w-16 h-16 text-white/50" />
            </div>
          )}
          
          <button
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/60 transition-all"
          >
            {isPlaying ? (
              <Pause className="w-16 h-16 text-white" />
            ) : (
              <Play className="w-16 h-16 text-white" />
            )}
          </button>

          {/* Menu */}
          <div className="absolute top-2 right-2">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-2 bg-black/50 hover:bg-black/70 rounded-full"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-10 w-40">
                <button
                  onClick={(e) => { e.stopPropagation(); handleReport(); }}
                  className="w-full px-4 py-2 text-left hover:bg-white/5 flex items-center gap-2 text-red-400"
                >
                  <Flag className="w-4 h-4" /> Signaler
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-4">
          {/* User info with profile picture */}
          <button
            onClick={() => navigate(`/profile/${podcast.user_id}`)}
            className="flex items-center space-x-2 mb-2 hover:opacity-80"
          >
            {podcast.user?.profile_image_url ? (
              <img 
                src={getMediaUrl(podcast.user.profile_image_url)} 
                alt={podcast.user?.name}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center">
                <span className="text-xs font-bold">{podcast.user?.name?.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div className="text-left">
              <span className="text-sm text-white flex items-center gap-1">{podcast.user?.name}{podcast.user?.is_verified && <VerifiedBadge size="xs" />}</span>
              {podcast.category && (
                <span className="text-xs text-gray-400">{podcast.category}</span>
              )}
            </div>
          </button>

          <h3 className="font-semibold line-clamp-3 mb-2 text-sm">{podcast.title}</h3>

          {/* Duration display */}
          {podcast.duration && (
            <div className="text-xs text-gray-400 mb-2">
              Durée: {Math.floor(podcast.duration / 60)}:{String(Math.floor(podcast.duration % 60)).padStart(2, '0')}
            </div>
          )}

          {/* Progress bar - interactive seek */}
          <div className="mb-3">
            <div 
              className="relative w-full bg-gray-700 rounded-full h-2.5 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (!audioRef.current || !duration) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = Math.max(0, Math.min(1, x / rect.width));
                audioRef.current.currentTime = percentage * duration;
                setCurrentTime(percentage * duration);
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
                if (!audioRef.current || !duration) return;
                const touch = e.touches[0];
                const rect = e.currentTarget.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const percentage = Math.max(0, Math.min(1, x / rect.width));
                audioRef.current.currentTime = percentage * duration;
                setCurrentTime(percentage * duration);
              }}
            >
              <div 
                className="absolute top-0 left-0 bg-gradient-to-r from-magenta-500 to-violet-500 h-full rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg transition-all"
                style={{ left: `calc(${progress}% - 8px)` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between border-t border-white/10 pt-3">
            <button onClick={handleLike} className={`flex items-center gap-1 ${liked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}>
              <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
              <span className="text-sm">{likesCount}</span>
            </button>
            
            <button onClick={handleShare} className="text-gray-400 hover:text-green-500">
              <Share2 className="w-5 h-5" />
            </button>
            
            <button onClick={handleSave} className={`${saved ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`}>
              <Bookmark className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>

        <audio
          ref={audioRef}
          src={getMediaUrl(podcast.audio_url)}
          onEnded={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
        />
      </div>
    </div>
  );
};

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Free Film Card Component (Admin content) - Style Pop Culture avec image portrait
export const FreeFilmCard = ({ film, playbackMode = 'browser' }) => {
  const [expanded, setExpanded] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  
  const youtubeId = getYouTubeVideoId(film.external_url);
  const isYouTube = !!youtubeId;
  const isNativePlatform = Capacitor.isNativePlatform();
  const inAppMode = playbackMode === 'inapp';
  
  const handleWatchClick = async () => {
    if (isYouTube && inAppMode) {
      // Admin chose "in-app": embedded YouTube player on every platform
      setShowPlayer(true);
    } else if (isYouTube && isNativePlatform) {
      // On iOS/Android native, open in InAppBrowser (integrated browser inside the app)
      try {
        await Browser.open({ 
          url: film.external_url,
          presentationStyle: 'fullscreen',
          toolbarColor: '#000000'
        });
      } catch (error) {
        console.error('Browser open error:', error);
        window.open(film.external_url, '_blank');
      }
    } else if (isYouTube) {
      // On web, show embedded player
      setShowPlayer(true);
    } else {
      // Non-YouTube link
      if (isNativePlatform) {
        try {
          await Browser.open({ 
            url: film.external_url,
            presentationStyle: 'fullscreen'
          });
        } catch (error) {
          window.open(film.external_url, '_blank');
        }
      } else {
        window.open(film.external_url, '_blank');
      }
    }
  };
  
  return (
    <>
      <div className="flex-shrink-0 w-[180px] snap-center">
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all h-full flex flex-col">
          {/* Image portrait */}
          <div className="relative aspect-[2/3]">
            {film.image_url ? (
              <img
                src={getMediaUrl(film.image_url)}
                alt={film.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                <Film className="w-12 h-12 text-white/50" />
              </div>
            )}
            
            {/* Title overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3">
              <h3 className="font-semibold text-sm line-clamp-2">{film.title}</h3>
            </div>
          </div>

          {/* Description and link below */}
          <div className="p-3 flex-1 flex flex-col">
            {film.description && (
              <div className="flex-1 mb-3">
                <p 
                  className={`text-xs text-gray-400 ${expanded ? '' : 'line-clamp-3'}`}
                  onClick={() => setExpanded(!expanded)}
                >
                  {film.description}
                </p>
                {film.description.length > 80 && (
                  <button 
                    onClick={() => setExpanded(!expanded)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 mt-1"
                  >
                    {expanded ? 'Voir moins' : 'Voir plus'}
                  </button>
                )}
              </div>
            )}
            
            {film.external_url && (
              <button
                onClick={handleWatchClick}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors w-full justify-center mt-auto ${
                  isYouTube 
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                    : 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30'
                }`}
              >
                {isYouTube ? <Play className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
                Regarder
              </button>
            )}
          </div>
        </div>
      </div>

      {/* YouTube Player Modal - web, or any platform when admin picked in-app mode */}
      {showPlayer && isYouTube && (inAppMode || !isNativePlatform) && (
        <YouTubePlayerModal 
          videoId={youtubeId} 
          title={film.title}
          externalUrl={film.external_url}
          onClose={() => setShowPlayer(false)} 
        />
      )}
    </>
  );
};

// YouTube Player Modal Component
const YouTubePlayerModal = ({ videoId, title, onClose, externalUrl }) => {
  const [hasError, setHasError] = useState(false);
  
  const openInYouTube = () => {
    const youtubeUrl = externalUrl || `https://www.youtube.com/watch?v=${videoId}`;
    window.open(youtubeUrl, '_blank');
    onClose();
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/95 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white truncate pr-4">{title}</h3>
          <div className="flex items-center gap-2">
            <button 
              onClick={onClose}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* YouTube iframe or Error fallback */}
        {hasError ? (
          <div className="relative w-full bg-gray-900 rounded-2xl p-8 text-center" style={{ paddingBottom: '56.25%' }}>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mb-4">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
              <p className="text-white mb-2 font-medium">Lecture impossible dans l'app</p>
              <p className="text-gray-400 text-sm mb-4">Cette vidéo ne peut pas être lue ici.</p>
              <button 
                onClick={openInYouTube}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-full transition-colors font-medium flex items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Regarder sur YouTube
              </button>
            </div>
          </div>
        ) : (
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 w-full h-full rounded-2xl"
              src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title={title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              onError={() => setHasError(true)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Article Card Component
const ArticleCard = ({ article, onOpen, onUpdate }) => {
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);

  const handleReport = async () => {
    try {
      await axios.post(`/api/explorer/articles/${article.id}/report`);
      toast.success('Article signalé');
      setShowMenu(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleShare = async () => {
    try {
      await shareContent({
        title: article.title || 'Actualité',
        text: article.content?.slice(0, 120) || `Lis cet article sur Destine`,
        url: buildShareUrl('article', article.id),
      });
    } catch (_e) {
      // already handled
    }
  };

  return (
    <div 
      id={`article-${article.id}`}
      onClick={onOpen}
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all cursor-pointer scroll-mt-24"
    >
      <div className="flex flex-col sm:flex-row">
        {article.cover_image_url && (
          <div className="sm:w-48 h-40 sm:h-auto flex-shrink-0">
            <img
              src={getMediaUrl(article.cover_image_url)}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center overflow-hidden">
                {article.user?.profile_image_url ? (
                  <img 
                    src={getMediaUrl(article.user.profile_image_url)} 
                    alt={article.user?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs font-bold">{article.user?.name?.charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div>
                <span className="text-sm font-medium">{article.user?.name}</span>
                {article.user?.is_verified_media && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                    Média
                  </span>
                )}
              </div>
            </div>
            
            <div className="relative">
              <button 
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                className="p-2 hover:bg-white/10 rounded-full"
              >
                <MoreVertical className="w-4 h-4" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-full mt-1 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-10">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleShare(); }}
                    className="w-full px-4 py-2 text-left hover:bg-white/5 flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" /> Partager
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleReport(); }}
                    className="w-full px-4 py-2 text-left hover:bg-white/5 flex items-center gap-2 text-red-400"
                  >
                    <Flag className="w-4 h-4" /> Signaler
                  </button>
                </div>
              )}
            </div>
          </div>

          <h3 className="font-bold text-lg mb-2">{article.title}</h3>
          <p className="text-sm text-gray-400 line-clamp-2 mb-2">{article.content?.slice(0, 150)}...</p>
          
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span>{new Date(article.created_at).toLocaleDateString('fr-FR')}</span>
            {article.audio_url && (
              <span className="flex items-center gap-1">
                <Mic className="w-4 h-4" /> Audio
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Article Card Horizontal Scroll - Image on top, content below
const ArticleCardHorizontal = ({ article, onOpen, onUpdate }) => {
  const [showMenu, setShowMenu] = useState(false);

  const handleReport = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`/api/explorer/articles/${article.id}/report`);
      toast.success('Article signalé');
      setShowMenu(false);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div 
      id={`article-${article.id}`}
      onClick={onOpen}
      className="flex-shrink-0 w-[280px] snap-center cursor-pointer scroll-mt-24 rounded-2xl"
    >
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-orange-500/30 transition-all h-full">
        {/* Image on top */}
        <div className="relative aspect-video">
          {article.cover_image_url ? (
            <img
              src={getMediaUrl(article.cover_image_url)}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
              <Newspaper className="w-12 h-12 text-white/50" />
            </div>
          )}
          
          {/* Audio badge */}
          {article.audio_url && (
            <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 rounded-full text-xs flex items-center gap-1">
              <Mic className="w-3 h-3" /> Audio
            </div>
          )}

          {/* Media badge */}
          {article.user?.is_verified_media && (
            <div className="absolute top-2 right-2 px-2 py-1 bg-blue-500/80 rounded-full text-xs">
              Média
            </div>
          )}

          {/* Menu */}
          <div className="absolute bottom-2 right-2">
            <button 
              onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <div className="absolute right-0 bottom-full mb-1 bg-gray-900 border border-white/10 rounded-xl shadow-xl z-10 w-32">
                <button
                  onClick={handleReport}
                  className="w-full px-3 py-2 text-left hover:bg-white/5 flex items-center gap-2 text-red-400 text-sm"
                >
                  <Flag className="w-3 h-3" /> Signaler
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content below */}
        <div className="p-3">
          {/* Author */}
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center overflow-hidden">
              {article.user?.profile_image_url ? (
                <img 
                  src={getMediaUrl(article.user.profile_image_url)} 
                  alt={article.user?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs font-bold">{article.user?.name?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <span className="text-xs text-gray-400 truncate">{article.user?.name}</span>
          </div>

          {/* Title */}
          <h3 className="font-semibold text-sm line-clamp-2 mb-1">{article.title}</h3>
          
          {/* Description */}
          <p className="text-xs text-gray-400 line-clamp-2 mb-2">{article.content?.slice(0, 80)}...</p>
          
          {/* Date */}
          <span className="text-xs text-gray-500">
            {new Date(article.created_at).toLocaleDateString('fr-FR')}
          </span>
        </div>
      </div>
    </div>
  );
};

// ==================== MODALS ====================

// Create Article Modal
const CreateArticleModal = ({ onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [extraImage, setExtraImage] = useState(null);
  const [audio, setAudio] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error('Titre et contenu requis');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      if (coverImage) formData.append('cover_image', coverImage);
      if (extraImage) formData.append('extra_image', extraImage);
      if (audio) formData.append('audio', audio);

      await axios.post('/api/explorer/articles', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Publier un article</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Titre *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500"
                placeholder="Titre de votre article"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Contenu *</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500 min-h-[200px]"
                placeholder="Rédigez votre article..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Image de couverture</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCoverImage(e.target.files[0])}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Image supplémentaire (optionnel)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setExtraImage(e.target.files[0])}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Audio (60s max, optionnel)</label>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => setAudio(e.target.files[0])}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
              />
              <p className="text-xs text-gray-400 mt-1">Pour une meilleure accessibilité</p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 rounded-full font-semibold disabled:opacity-50"
              >
                {uploading ? 'Publication...' : 'Publier'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


// Article Viewer Modal
const ArticleViewerModal = ({ article, onClose, onUpdate }) => {
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const audioRef = useRef(null);
  const viewRecorded = useRef(false);

  const loadComments = async () => {
    try {
      const res = await axios.get(`/api/explorer/articles/${article.id}/comments`);
      setComments(res.data);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  useEffect(() => {
    loadComments();
    
    // Record view after 3 seconds of reading
    const viewTimer = setTimeout(async () => {
      if (!viewRecorded.current) {
        try {
          await axios.post(`/api/explorer/articles/${article.id}/view`);
          viewRecorded.current = true;
        } catch (e) {
          // Ignore view recording errors
        }
      }
    }, 3000);
    
    return () => clearTimeout(viewTimer);
  }, [article.id]);

  const handleComment = async () => {
    if (!comment.trim()) return;
    try {
      await axios.post(`/api/explorer/articles/${article.id}/comments`, { content: comment });
      setComment('');
      loadComments();
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-white/10 p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center overflow-hidden">
              {article.user?.profile_image_url ? (
                <img 
                  src={getMediaUrl(article.user.profile_image_url)} 
                  alt={article.user?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="font-bold">{article.user?.name?.charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div>
              <span className="font-medium">{article.user?.name}</span>
              {article.user?.is_verified_media && (
                <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                  Média vérifié
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Cover Image */}
          {article.cover_image_url && (
            <img
              src={getMediaUrl(article.cover_image_url)}
              alt={article.title}
              className="w-full h-64 object-cover rounded-2xl mb-6"
            />
          )}

          {/* Title */}
          <h1 className="text-3xl font-bold mb-4">{article.title}</h1>

          {/* Audio Player */}
          {article.audio_url && (
            <div className="mb-6 p-4 bg-white/5 rounded-xl">
              <p className="text-sm text-gray-400 mb-2">🎧 Écouter l&apos;article</p>
              <audio
                ref={audioRef}
                src={getMediaUrl(article.audio_url)}
                controls
                className="w-full"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-invert max-w-none mb-6">
            <p className="text-gray-300 whitespace-pre-wrap">{article.content}</p>
          </div>

          {/* Extra Image */}
          {article.extra_image_url && (
            <img
              src={getMediaUrl(article.extra_image_url)}
              alt="Illustration"
              className="w-full h-auto rounded-xl mb-6"
            />
          )}

          {/* Comments Section */}
          <div className="border-t border-white/10 pt-6">
            <h3 className="font-semibold mb-4">Commentaires ({comments.length})</h3>
            
            {/* Add comment */}
            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Ajouter un commentaire..."
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-orange-500"
              />
              <button
                onClick={handleComment}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-xl font-medium"
              >
                Envoyer
              </button>
            </div>

            {/* Comments list */}
            <div className="space-y-4">
              {comments.map(c => (
                <div key={c.id} className="flex gap-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold">{c.user?.name?.charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{c.user?.name}</span>
                      <span className="text-xs text-gray-500">
                        {new Date(c.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <p className="text-gray-300 text-sm">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Explorer;
